import { useState, useRef, useCallback, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, User, Phone, Plus, Loader2 } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import CreateDriverSheet from './CreateDriverSheet';

const PAGE_SIZE = 15;

interface Driver {
    id: number;
    first_name: string;
    last_name: string;
    phone: string;
    photo?: string;
}

interface SelectDriverStepProps {
    selectedDriver: Driver | null;
    onSelect: (driver: Driver | null) => void;
}

export default function SelectDriverStep({
    selectedDriver,
    onSelect,
}: SelectDriverStepProps) {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [createSheetOpen, setCreateSheetOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const {
        data,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteQuery({
        queryKey: ['drivers-search', debouncedSearch],
        queryFn: async ({ pageParam = 0 }) => {
            // For initial list: exclude drivers with active (non-COMPLETED) contracts
            let occupiedDriverIds: number[] = [];
            if (!debouncedSearch) {
                const { data: contracts } = await supabase
                    .from('contracts')
                    .select('driver_id, contract_updates(status, created_at)')
                    .not('driver_id', 'is', null);

                // Only exclude drivers whose latest contract status is NOT 'COMPLETED'
                occupiedDriverIds = contracts
                    ?.filter((c: any) => {
                        const updates = c.contract_updates || [];
                        if (updates.length === 0) return true; // No updates = still in progress
                        const latestStatus = [...updates].sort(
                            (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                        )[0];
                        return latestStatus?.status !== 'COMPLETED';
                    })
                    .map((c: any) => c.driver_id) || [];
            }

            // Query profiles with driver_id — THE DRIVER'S OWN ROW AND NEVER AN ADMIN'S.
            // `profiles.driver_id` is unique only WHERE `admin_id IS NULL`, so an admin holding a
            // test profile against a driver id is listed here as a second, differently-named
            // candidate for the SAME driver — and picking it starts a contract in the wrong name.
            let query = supabase
                .from('profiles')
                .select('driver_id, first_name, last_name, phone, photo')
                .not('driver_id', 'is', null)
                .is('admin_id', null);

            // For initial list: exclude drivers with active contracts
            if (!debouncedSearch && occupiedDriverIds.length > 0) {
                query = query.not('driver_id', 'in', `(${occupiedDriverIds.join(',')})`);
            }

            // Apply search filter (searches entire driver/profile table)
            if (debouncedSearch) {
                query = query.or(
                    `first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%,phone.ilike.%${debouncedSearch}%`
                );
            }

            // Order and paginate
            query = query
                .order('driver_id', { ascending: false })
                .range(pageParam * PAGE_SIZE, (pageParam + 1) * PAGE_SIZE - 1);

            const { data, error } = await query;
            if (error) throw error;

            const drivers = data?.map((p: any) => ({
                id: p.driver_id,
                first_name: p.first_name || '',
                last_name: p.last_name || '',
                phone: p.phone || '',
                photo: p.photo,
            })) || [];

            return {
                drivers,
                nextPage: drivers.length === PAGE_SIZE ? pageParam + 1 : undefined,
            };
        },
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 0,
    });

    // Flatten all pages into a single drivers array
    const drivers = data?.pages.flatMap((page) => page.drivers) || [];

    // Scroll handler for infinite loading
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || isFetchingNextPage || !hasNextPage) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
    };

    return (
        <>
            <div className="h-full flex flex-col space-y-4 p-1">
                {/* Fixed Header Section */}
                <div className="flex-none space-y-4">
                    {/* Search input with create button */}
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by name or phone..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Button variant="outline" onClick={() => setCreateSheetOpen(true)}>
                            <Plus className="h-4 w-4 mr-1" />
                            New
                        </Button>
                    </div>

                    {/* Selected driver card */}
                    {selectedDriver && (
                        <Card className="border-primary bg-primary/5">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-4">
                                    <Avatar className="h-12 w-12">
                                        <SignedAvatarImage src={selectedDriver.photo} />
                                        <AvatarFallback>
                                            {getInitials(selectedDriver.first_name, selectedDriver.last_name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                        <p className="font-medium">
                                            {selectedDriver.first_name} {selectedDriver.last_name}
                                        </p>
                                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                                            <Phone className="h-3 w-3" />
                                            {selectedDriver.phone}
                                        </p>
                                    </div>
                                    <span className="text-xs text-primary font-medium">Selected</span>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Scrollable Driver list with infinite scroll */}
                <div
                    ref={scrollContainerRef}
                    className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-2"
                >
                    {isLoading ? (
                        [...Array(5)].map((_, i) => (
                            <Skeleton key={i} className="h-16 w-full" />
                        ))
                    ) : drivers?.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <User className="h-10 w-10 mx-auto mb-2 opacity-50" />
                            <p>No drivers found</p>
                            <Button variant="link" onClick={() => setCreateSheetOpen(true)} className="mt-2">
                                Create a new driver
                            </Button>
                        </div>
                    ) : (
                        <>
                            {drivers?.map((driver: Driver) => (
                                <Card
                                    key={driver.id}
                                    className={`cursor-pointer transition-colors hover:bg-muted/50 ${selectedDriver?.id === driver.id ? 'border-primary bg-primary/5' : ''
                                        }`}
                                    onClick={() => onSelect(driver)}
                                >
                                    <CardContent className="p-3">
                                        <div className="flex items-center gap-3">
                                            <Avatar className="h-10 w-10">
                                                <SignedAvatarImage src={driver.photo} />
                                                <AvatarFallback className="text-sm">
                                                    {getInitials(driver.first_name, driver.last_name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="font-medium text-sm">
                                                    {driver.first_name} {driver.last_name}
                                                </p>
                                                <p className="text-xs text-muted-foreground">{driver.phone}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                            {isFetchingNextPage && (
                                <div className="flex justify-center py-4">
                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <CreateDriverSheet
                open={createSheetOpen}
                onOpenChange={setCreateSheetOpen}
                onSuccess={onSelect}
            />
        </>
    );
}
